import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@zg-rentals/react-query5';
import { useHpApiSavedSearchV3Handler_create } from '@zg-rentals/ts-hotpads-api-client-react-query';
import { user } from '@/src/lib/api/endpoints/user';
import type { SavedSearchItem } from '@/src/lib/api/endpoints/user';

import { savedSearchKeys } from './queryKeys';

const SAVED_SEARCH_TAG = 'savedSearches';

const savedSearchesQueryOptions = () => ({
  queryKey: savedSearchKeys.list(),
  queryFn: async () => {
    const response = await user.getSavedSearches();
    return response.data;
  },
  staleTime: 30_000,
  meta: {
    tags: [SAVED_SEARCH_TAG],
    context: { api: 'hotpads-api', details: 'fetch saved searches' },
  },
});

export const useSavedSearches = (options?: { enabled?: boolean }) => {
  return useQuery({
    ...savedSearchesQueryOptions(),
    enabled: options?.enabled,
  });
};

/**
 * Reads the signed-in user's saved searches on demand, bypassing the cache — for a check that
 * must see the account as it is now, like one the user has only just signed in to. Shares the
 * list's cache entry, so `useSavedSearches` readers update with it.
 */
export const useFetchSavedSearches = () => {
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.fetchQuery({ ...savedSearchesQueryOptions(), staleTime: 0 }), [queryClient]);
};

/**
 * Create a saved search through v3. The alert goes to the session's account email, so the
 * caller must be signed in first. Envelope failures reject: branch on `v3ErrorReason`.
 */
export const useCreateSavedSearch = () => {
  return useHpApiSavedSearchV3Handler_create({
    meta: {
      invalidates: [SAVED_SEARCH_TAG],
      context: { api: 'hotpads-api', details: 'create saved search v3' },
    },
  });
};

export const useUpdateSavedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SavedSearchItem) => user.updateSavedSearch(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchKeys.all });
    },
  });
};

export const useDeleteSavedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (searchId: string) => user.deleteSavedSearch(searchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedSearchKeys.all });
    },
  });
};
